/**
 * React context exposing the shared `ContentTypeEventService` (cross-product
 * command bus from `@confluence/content-types-bridge`) to descendants of
 * `ContentTypesHeader` without prop-drilling.
 *
 * Provider is `WhiteboardShell`. Consumers call `useContentTypeEventService()`,
 * which throws if called outside a provider — this is intentional: the hook
 * should only be used in components that are always rendered inside
 * `WhiteboardShell` (e.g. `SlidesExportMenuItem`).
 *
 * The shape is a minimal structural interface to avoid a circular package
 * dependency with `@confluence/content-types-bridge`.
 */
import { createContext, useContext } from 'react';

import type {
	ContentTypeCommand,
	ContentTypeProductKey,
} from '@confluence/content-types-bridge-types/entry-points/contentTypeBridgeSharedTypes.ts';

/** Structural subset of `ContentTypeEventService`. Widen if more methods are needed. */
export type ContentTypeEventServiceShape = {
	mountEventService: (productKey: ContentTypeProductKey) => {
		sendCommand: (message: ContentTypeCommand) => void;
	};
};

export const ContentTypeEventServiceContext = createContext<ContentTypeEventServiceShape | null>(
	null,
);

/**
 * Returns the `ContentTypeEventService` instance from context.
 * Throws if called outside a `ContentTypeEventServiceContext.Provider`.
 */
export const useContentTypeEventService = (): ContentTypeEventServiceShape => {
	const eventService = useContext(ContentTypeEventServiceContext);
	if (!eventService) {
		throw new Error(
			'useContentTypeEventService must be used within a ContentTypeEventServiceContext.Provider.',
		);
	}
	return eventService;
};
